import React, { type FC } from 'react';

import { motion } from 'framer-motion';

import { Tabs, TabsItem } from '@eventbrite/marmalade';
import { type DayPart, type SessionsGroupedByTimeOfDay } from '../../types';

import classNames from 'classnames';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../../utils/rebrand';
import type { InfiniteCalendarSeriesProps } from '../../../InfiniteCalendarSeries/InfiniteCalendarSeries';
import SimpleTimeSlotList from '../SimpleTimeSlotList/SimpleTimeSlotList';
import { filterTabHeadersBySessionsInDayPart, hasTimeSlots } from '../utils';
import commonStyles from './TimeSlotTabHeader.common.module.scss';
import defaultStyles from './TimeSlotTabHeader.module.scss';
import rebrandStyles from './TimeSlotTabHeader.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

type TimeSlotTabHeaderProps = {
    activeTab?: keyof DayPart;
    onSessionClick: (sessionId: string, sessionLabel: string) => void;
    isAppRebrandActive: boolean;
    sessionsByDurationAndTimeOfDay: SessionsGroupedByTimeOfDay;
    setActiveTab: (tab: keyof DayPart) => void;
} & Pick<InfiniteCalendarSeriesProps, 'timezone' | 'locale'>;

export const TimeSlotTabHeader: FC<TimeSlotTabHeaderProps> = ({
    onSessionClick,
    activeTab,
    sessionsByDurationAndTimeOfDay,
    isAppRebrandActive,
    setActiveTab,
    timezone,
    locale,
}) => {
    if (!hasTimeSlots(sessionsByDurationAndTimeOfDay)) {
        return null;
    }

    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    const sessions = activeTab ? sessionsByDurationAndTimeOfDay[activeTab] : [];
    const tabItems = filterTabHeadersBySessionsInDayPart(
        sessionsByDurationAndTimeOfDay,
    ).map((tab) => (
        <TabsItem
            className={classNames(commonStyles.tabItem, styles.tabItem)}
            key={tab}
            value={tab}
        >
            {tab}
        </TabsItem>
    ));

    return (
        <div
            className={commonStyles.tabContainer}
            data-testid="time-slot-tab-header"
        >
            <div
                className={classNames(commonStyles.tabHeader, styles.tabHeader)}
            >
                <Tabs
                    aria-label="category options"
                    data-testid="time-slot-tab"
                    value={activeTab}
                    onValueChange={(value) =>
                        setActiveTab(value as keyof DayPart)
                    }
                >
                    {tabItems}
                </Tabs>
            </div>
            <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
            >
                <SimpleTimeSlotList
                    onSessionClick={onSessionClick}
                    sessions={sessions}
                    timezone={timezone}
                    locale={locale}
                    isAppRebrandActive={isAppRebrandActive}
                />
            </motion.div>
        </div>
    );
};
